/**
 * @description
 * Test deployment component for marketplace configuration.
 * Provides interface for testing deployment configurations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { TestDeploymentProps } from './types';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';

/**
 * Test deployment component
 */
export function TestDeployment({ onRefresh: _onRefresh }: TestDeploymentProps) {
  const { testDeployment, deploymentTestResult } = useMarketplaceConfig();
  const [isTesting, setIsTesting] = useState(false);

  const handleTestDeployment = async () => {
    setIsTesting(true);
    try {
      await testDeployment();
    } catch (error) {
      console.error('Error testing deployment:', error);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Test Deployment</h2>
        <p className="text-sm text-muted-foreground">
          Test your marketplace configuration by attempting a test deployment.
        </p>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg border bg-card p-6">
          <h3 className="text-lg font-semibold mb-4">Deployment Test</h3>
          <p className="text-sm text-muted-foreground mb-4">
            This will test your Cloudflare credentials and deployment settings by creating a test worker.
          </p>
          
          <button
            onClick={handleTestDeployment}
            disabled={isTesting}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
          >
            {isTesting ? 'Testing...' : 'Run Test Deployment'}
          </button>
        </div>

        {deploymentTestResult && (
          <div className="rounded-lg border bg-card p-6">
            <h3 className="text-lg font-semibold mb-4">Test Results</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Status:</span>
                <span className={`text-sm ${
                  deploymentTestResult.success ? 'text-green-600' : 'text-red-600'
                }`}>
                  {deploymentTestResult.success ? 'Success' : 'Failed'}
                </span>
              </div>
              {deploymentTestResult.message && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Message:</span>
                  <span className="text-sm text-muted-foreground">
                    {deploymentTestResult.message}
                  </span>
                </div>
              )}
              {deploymentTestResult.workerUrl && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Worker URL:</span>
                  <a 
                    href={deploymentTestResult.workerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    {deploymentTestResult.workerUrl}
                  </a>
                </div>
              )}
              {deploymentTestResult.details && (
                <div className="mt-4">
                  <span className="text-sm font-medium">Details:</span>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
                    {JSON.stringify(deploymentTestResult.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}